import React, { useEffect, useState } from "react";
import { Switch, Tooltip } from "antd";
import { Line } from "react-chartjs-2";
import { COLORS } from "../../../consts";

const LineChart = ({
  lineData,
  currentMetric,
  siteMetadata,
  isCumulative,
  setIsCumulative,
}) => {
  const [hiddenIndexes, setHiddenIndexes] = useState(new Set());
  const [data, setData] = useState({ labels: [], datasets: [] });

  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };
  const getMetricLabel = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.label;
    }
    return "Unknown Metric";
  };

  const getMetricValue = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.value;
    }
    return "Unknown Metric";
  };
  useEffect(() => {
    // Custom hidden support
    const filteredLines =
      lineData?.data?.filter((_, idx) => !hiddenIndexes.has(idx)) || [];
    setData({
      labels:
        lineData.labels?.map((label) =>
          new Date(label).toString().split(" ").slice(1, 4).join(" "),
        ) || [],
      datasets:
        (filteredLines || []).map((line, idx) => ({
          label: line.name || "",
          data: line.val || [],
          backgroundColor: COLORS.graphColors[idx % COLORS.graphColors.length],
          borderColor: COLORS.graphColors[idx % COLORS.graphColors.length],
          borderWidth: 2,
          borderJoinStyle: "round",
          lineTension: 0.5,
        })) || [],
    });
  }, [lineData, hiddenIndexes]);

  // Hide/show series
  const toggleSeries = (idx) => {
    setHiddenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const options = {
    responsive: true,
    spanGaps: true,
    elements: { line: { borderJoinStyle: "round" } },
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: `${getMetricLabel(currentMetric)} ${
            siteMetadata?.units &&
            siteMetadata?.units[getMetricValue(currentMetric)]
              ? `(${siteMetadata?.units[getMetricValue(currentMetric)]})`
              : ""
          }`,
          font: { family: "'museoSansBold', sans-serif", size: 16 },
        },
      },
      x: {
        title: {
          display: true,
          text: "Date",
          font: { family: "'museoSansBold', sans-serif", size: 16 },
        },
      },
    },
  };

  return (
    <div
      className="flex flex-col items-center mb-10 py-4 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
      id="linechart"
    >
      <div id="linecontent" className="w-full max-w-[1120px] overflow-x-scroll">
        <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
          {getMetricLabel(currentMetric)}
        </p>
        {/* ---------- CUSTOM LEGEND ---------- */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3 custom-legend justify-center">
          {(lineData?.data || []).map((ds, idx) => {
            const hidden = hiddenIndexes.has(idx);
            const short =
              ds.name?.length > 18 ? ds.name.slice(0, 15) + "…" : ds.name;
            return (
              <div
                key={ds.name || idx}
                className="flex items-center cursor-pointer select-none"
                onClick={() => toggleSeries(idx)}
                title={ds.name}
                style={{ opacity: hidden ? 0.3 : 1 }}
              >
                <Tooltip title={ds.name} placement="top">
                  <div className="flex items-center">
                    <div
                      className="relative w-10 h-0.5 mr-2"
                      style={{
                        backgroundColor:
                          COLORS.graphColors[idx % COLORS.graphColors.length],
                        opacity: hidden ? 0.3 : 1,
                      }}
                    >
                      <div
                        className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            COLORS.graphColors[idx % COLORS.graphColors.length],
                          opacity: hidden ? 0.3 : 1,
                        }}
                      />
                    </div>
                    <span className="text-sm">{short}</span>
                  </div>
                </Tooltip>
              </div>
            );
          })}
        </div>
        <div id="linecontrols" className="flex justify-end mb-5">
          {siteMetadata?.cumulative_metrics?.includes(
            getMetricValue(currentMetric),
          ) && (
            <div className="flex items-center cumulative">
              <p className="text-sm  pr-2 ">Enable Cumulative</p>
              <Switch checked={isCumulative} onChange={setIsCumulative} />
            </div>
          )}
        </div>
        <div className="flex justify-center">
          <Line data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default LineChart;
